/**
 * A situação calculada pelo painel (E203.F2, ADR-0146 §4 e §6; ADR-0136): o resultado que uma
 * revisão do perito já cobre (aceite do crédito e da integridade, face, contraprova, suprimento,
 * diligências, autorização) abre os pontos daquele tipo, com o formulário de sempre
 * (`PontosParaRevisao`: decisão e fundamento obrigatório). Sem ponto aberto, diz onde estão as
 * revisões registradas.
 */
import { Link } from 'react-router-dom';

import { PontosParaRevisao } from '@/components/Formalizacao/PontosParaRevisao';
import { ptBR } from '@/i18n/pt-BR';
import type { AvaliacaoDaFormalizacaoData } from '@/lib/formalizacaoAvaliacao';
import type { RevisaoDaOrigem } from '@/lib/documentoDaSaida';
import { useAvaliacaoDaFormalizacao } from '@/lib/useAvaliacaoDaFormalizacao';
import { useParecerFacial } from '@/lib/useParecerFacial';

import type { PropsDoConteudo } from './tipos';

const t = ptBR.painelDoDado;

function soDoTipo(a: AvaliacaoDaFormalizacaoData, tipo: RevisaoDaOrigem) {
  return {
    ...a,
    revisoes_pendentes: a.revisoes_pendentes.filter((r) => r.tipo === tipo),
    revisoes_facultativas: (a.revisoes_facultativas ?? []).filter((r) => r.tipo === tipo),
  };
}

export function RevisaoNoPainel({
  client,
  caseId,
  tipo,
  acoes,
  onMudou,
}: PropsDoConteudo & { tipo: RevisaoDaOrigem }) {
  const { avaliacao, envio, revisar } = useAvaliacaoDaFormalizacao(client, caseId, tipo);
  const versao = avaliacao.estado === 'pronto' ? avaliacao.dados.avaliacao.versao : 0;
  const parecerFacial = useParecerFacial(client, caseId, versao);
  if (avaliacao.estado !== 'pronto') {
    const texto = avaliacao.estado === 'erro' ? t.erro : t.carregando;
    return <p className="text-body-sm text-fg-muted">{texto}</p>;
  }
  const gravada = avaliacao.dados.avaliacao;
  const doTipo = soDoTipo(gravada.conteudo.avaliacao, tipo);
  const algum = doTipo.revisoes_pendentes.length + doTipo.revisoes_facultativas.length > 0;
  return (
    <div className="space-y-sc-3" data-testid="painel-revisao" data-tipo={tipo}>
      <p className="text-body-sm text-fg-muted">{t.revisao.texto}</p>
      {algum ? (
        <PontosParaRevisao
          avaliacao={doTipo}
          versao={gravada.versao}
          rever={null}
          enviando={envio.estado === 'enviando' ? envio.objeto : null}
          onRegistrar={async (revisao) => {
            const aceita = await revisar(revisao);
            if (aceita) {
              acoes.salvo();
              onMudou();
            }
            return aceita;
          }}
          onCancelarRever={() => undefined}
          parecerFacial={parecerFacial}
        />
      ) : (
        <p className="text-body-sm" data-testid="painel-revisao-sem-ponto">
          {t.revisao.semPonto}{' '}
          <Link
            to={{ hash: 'conclusao' }}
            className="text-accent-700 underline"
            onClick={acoes.fechar}
          >
            {t.revisao.abrirConclusao}
          </Link>
        </p>
      )}
    </div>
  );
}
